import "./src/index.css";
import "./src/styles/game-system.css";
// Uncomment necessary imports
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "./src/components/ui/tooltip";
import { Toaster } from "./src/components/ui/toaster";
import { Toaster as Sonner } from "./src/components/ui/sonner";
import { useEffect, useState } from "react";
// Import the Theme Provider
import GameThemeProvider from './src/components/character/ThemeProvider';

// Create a client with performant settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// ClientOnly wrapper component to prevent hydration issues
const ClientOnly = ({ children, ...delegated }) => {
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);
  
  if (!hasMounted) {
    // Return a placeholder to prevent hydration mismatches for client-only components
    // Using visibility hidden keeps the layout intact
    return <div style={{ visibility: 'hidden' }} {...delegated}>{children}</div>;
  }
  
  return <div {...delegated}>{children}</div>;
};

// Wrap the root element to provide theme or other global context
export const wrapRootElement = ({ element }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Wrap with GameThemeProvider consistent with gatsby-ssr.js */}
      <GameThemeProvider>
        <TooltipProvider>
          <Toaster />
          {/* Sonner likely needs ClientOnly if it uses browser APIs on mount */}
          <ClientOnly>
            <Sonner />
          </ClientOnly>
          {/* The actual page content */}
          {element}
        </TooltipProvider>
      </GameThemeProvider>
    </QueryClientProvider>
  );
};

// Let Gatsby know this file is used to inject client-side only functionality
// This helps Gatsby optimize its build process
// export const disableCorePrefetching = () => true; // Keep disabled unless needed

// Use onRouteUpdate for analytics or other page change effects
export const onRouteUpdate = ({ location }) => {
  // You can track page views here or perform other actions on route change
  if (process.env.NODE_ENV === "production") {
    // Only log in production to reduce console noise in development
    console.log(`Page loaded: ${location.pathname}`);
  }
};

// Prefetch resources on hover/focus
// export const onPrefetchPathname = ({ pathname }) => { // Keep disabled unless needed
//   // This is handled automatically by Gatsby
// };

// Improve client-side navigation speed
export const shouldUpdateScroll = ({
  routerProps: { location },
  prevRouterProps,
}) => {
  // const { pathname } = location; // pathname declared but not used, safe to remove if not needed

  // If it's a new page, scroll to top
  // Checking prevRouterProps is sufficient
  if (!prevRouterProps) {
    // scroll to top
    return true; // Returning true scrolls to top by default
  }

  // Return false to prevent scroll for internal anchor links
  if (location.hash) {
    return false;
  }

  // Default behavior
  return true;
};
